import {
  Box,
  Center,
  Container,
  Flex,
  Grid,
  Heading,
  SystemStyleObject,
  Text,
} from '@chakra-ui/react';
import { ButtonFullWidthMobile } from '@components/Button';
import { MarqueeItem } from '@components/MarqueeAnimation';
import { StarBackground } from '@components/StarBackground';
import {
  APP_ROUTES_ABSOLUTE,
  AppLink,
  AppLinkWithArrow,
  fullSizeParentStyles,
  GradientShadowOverlay,
  Image,
  LANDING_ROUTES,
  LinksWithCuttedBorders,
  pxToRem,
  SHARED_ROUTES,
} from '@cyberkongz/ui';
import { SliderSection } from '@features/playAndKollect/SliderSection';
import { PageSEOTags } from '@features/seo/SEO';
import { PropsWithChildren } from 'react';

const EXPERIENCE_ITEMS = [
  {
    title: 'MIGRATION TO RONIN',
    description:
      'With the migration to Ronin, our web3 gaming experience, Play & Kollect stays ahead of the curve and sets foot on the largest web3 gaming blockchain, securing minimal gas fees and a smooth experience.',
  },
  {
    title: 'FULLY ON-CHAIN WEB3 GAMING',
    description:
      'Play & Kollect is a complete on-chain web3 gaming experience, ensuring the game system is transparent and fair.',
  },
  {
    title: 'READY FOR THE FUTURE OF WEB3 GAMING',
    description:
      'Play & Kollect has been redesigned for the launch on Ronin Network. The entire web3 gaming experience comes to life in more exciting ways, along with new infrastructure introductions that enable future game expansions and collaborations.',
  },
];

function KongiumWithRadialGradient({
  stylesContainer,
  children,
}: PropsWithChildren & { stylesContainer?: SystemStyleObject }) {
  return (
    <Box css={{ position: 'absolute', zIndex: -1, ...stylesContainer }}>
      <Box
        css={{
          position: 'relative',
          '&:before': {
            position: 'absolute',
            zIndex: -1,
            display: 'block',
            content: '""',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            w: '90%',
            h: '90%',
            bg: 'radial-gradient(50% 50% at 50% 50%, rgba(70, 240, 251, 0.7) 0%, rgba(70, 240, 251, 0) 100%)',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function WelcomeSection() {
  return (
    <Box width="100%" position="relative">
      <Box
        css={{
          ...fullSizeParentStyles,
          objectFit: ['cover'],
          height: [pxToRem(600), '100%'],
          zIndex: -1,
          objectPosition: ['top 0 right 50%', 'top -80px right 50%', 'center'],
          backgroundImage: 'url(/static/play_and_kollect/slider/bg_temple.jpg)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Container
        css={{
          position: 'relative',
          zIndex: 2,
          pt: [pxToRem(190), null, '20vh'],
          pb: [20, undefined, pxToRem(184)],
        }}
      >
        <Center>
          <Image
            src="/static/play_and_kollect/pnk_logo_white.png"
            alt="pnk logo"
            maxW={[pxToRem(275), pxToRem(550)]}
            w="100%"
          />
        </Center>
        <Box css={{ position: 'relative' }} mt={[pxToRem(190), '25vh']}>
          <KongiumWithRadialGradient
            stylesContainer={{ top: [pxToRem(-80), pxToRem(-35)], left: pxToRem(-60) }}
          >
            <Image src="/static/play_and_kollect/intro_kongium_small.png" alt="Small Kongium Ore" />
          </KongiumWithRadialGradient>
          <KongiumWithRadialGradient
            stylesContainer={{
              top: 0,
              right: 0,
              transform: ['translate(70%, 25%)', 'translate(50%, 25%)'],
            }}
          >
            <Image src="/static/play_and_kollect/intro_kongium_big.png" alt="Big Kongium Ore" />
          </KongiumWithRadialGradient>
          <Heading css={{ maxWidth: '10em', lineHeight: 'base' }}>
            the ultimate onchain web3
          </Heading>
          <Heading variant="displayPrimary">gaming experience</Heading>
          <Flex
            css={{
              mt: [0, pxToRem(60)],
              borderTop: ['none', 'thin'],
              flexDirection: ['column', 'row'],
            }}
          >
            {EXPERIENCE_ITEMS.map(({ title, description }) => (
              <Box
                key={title}
                css={{
                  py: pxToRem(30),
                  px: [0, 4, pxToRem(30)],
                  borderRight: ['none', 'thin'],
                  borderBottom: ['thin', 'none'],
                  '&:first-of-type': { pl: 0 },
                  '&:last-of-type': { pr: 0, borderRight: 'none', borderBottom: 'none' },
                }}
              >
                <Heading size="2xs">{title}</Heading>
                <Box css={{ mt: 4, color: 'textSecondary' }}>{description}</Box>
              </Box>
            ))}
          </Flex>
        </Box>
      </Container>
      <GradientShadowOverlay
        css={{
          bottom: ['auto', 0],
          top: [pxToRem(475), 'auto'],
          height: [pxToRem(140), pxToRem(380), pxToRem(380)],
        }}
      />
    </Box>
  );
}

const ASSETS_ITEMS = [
  {
    name: 'kongium',
    image: 'asset_kongium.png',
    imageGradient: 'radial-gradient(50% 50% at 50% 50%, #46F0FB 0%, rgba(70, 240, 251, 0) 100%)',
    description: (
      <>
        Kongium is an integral web3 gaming resource in the Play & Kollect ecosystem. Collect Kongium
        on Adventure Runs and use it to level up your VX and redeem rewards from the Treasury.
        Kongium is a soulbound token and cannot be transferred between wallets.
      </>
    ),
    cardGradient: 'radial-gradient(92.37% 106.22% at 80.24% 100%, #49E5E5 0%, #000000 100%)',
    href: '/play/kongium',
  },
  {
    name: 'kredits',
    image: 'asset_kredit.png',
    imageGradient:
      'radial-gradient(50% 50% at 50% 50%, rgba(251, 204, 58, 0.7) 0%, rgba(251, 204, 58, 0) 100%);',
    description: (
      <>
        Kredits are the primary in-game currency used in the web3 gaming experience: Play & Kollect.
        They are used to fuel adventure runs and are a non-transferable asset. Each CyberKongz VX
        can claim a daiy bonus of 100 Kredits once every 24 hours. Fuel rods have been completely
        replaced by Kredits.
        <br />
        <br />
        Users can swap $BANANA for Kredits (1 $BANANA = 1000 Kredits).
      </>
    ),
    cardGradient:
      'radial-gradient(92.37% 106.22% at 80.24% 100%, rgba(251, 204, 58, 0.7) 0%, rgba(251, 204, 58, 0) 100%)',
    href: '/play/kredits',
  },
  {
    name: 'encounters',
    image: 'asset_encounters.png',
    imageGradient:
      'radial-gradient(50% 50% at 50% 50%, rgba(207, 49, 3, 0.7) 0%,rgba(207, 49, 3, 0) 100%)',
    description: (
      <>
        The ultimate web3 gaming battle! During Adventure Runs players will encounter enemies, the
        encounter can either be won or lost. The outcome is dependent on Gear Score. Higher tier
        Adventure Runs will increase the risk of encountering stronger enemies. Beat the enemy to
        win rewards such as Mystery Boxes or Kredits.
      </>
    ),
    cardGradient:
      'radial-gradient(92.37% 106.22% at 80.24% 100%, rgba(207, 49, 3, 0.7) 0%, rgba(207, 49, 3, 0) 100%)',
    href: '/kollect/encounters',
  },
  {
    name: 'gear',
    image: 'asset_gear.png',
    imageGradient:
      'radial-gradient(50% 50% at 50% 50%, rgba(56, 173, 253, 0.7) 0%, rgba(56, 173, 253, 0) 100%)',
    description: (
      <>
        Equip Gear (Armor and Weapons) to boost your Gear Score and increases your chances at
        winning better loot. The rarer the Gear you equip, the better the Gear Score. You can redeem
        gear from Loot capsules found on Adventure Runs or purchase Gear from{' '}
        <AppLink href={SHARED_ROUTES.MAVIS_MARKET_COLLECTION_VX_GEAR} textDecoration="underline">
          Mavis Market
        </AppLink>
        .
      </>
    ),
    cardGradient:
      'radial-gradient(92.37% 106.22% at 80.24% 100%, rgba(56, 173, 253, 0.7) 0%, rgba(56, 173, 253, 0) 100%)',
    href: '/play/gear-score-gs',
  },
  {
    name: 'tiered runs',
    image: 'asset_adventures.png',
    imageGradient:
      'radial-gradient(50% 50% at 50% 50%, rgba(145, 53, 253, 0.7) 0%, rgba(145, 53, 253, 0) 100%)',
    description: (
      <>
        Players can now elect to go on different types of Adventure Runs. There are four tiers of
        Adventure Runs players can choose from. Each tier has varying difficulty levels and Gear
        Score requirements, with higher tiers offering better rewards.
        <br />
        <br />
        Tiered adventure runs allow the web3 gaming experience to have fairer distribution of loot
        and rewards.
      </>
    ),
    cardGradient:
      'radial-gradient(92.37% 106.22% at 80.24% 100%, rgba(145, 53, 253, 0.7) 0%, rgba(145, 53, 253, 0) 100%)',
    href: '/play/adventure-runs',
  },
];

function AssetCard({
  bg,
  bg1,
  image,
  alt,
  description,
  href,
}: {
  bg: string;
  bg1: string;
  image: string;
  alt: string;
  description: JSX.Element;
  href: string;
}) {
  return (
    <Flex
      css={{
        border: 'thin',
        position: 'relative',
      }}
      w={['100%', pxToRem(380)]}
      mb={[4, 20, pxToRem(120)]}
    >
      <Box
        css={{
          ...fullSizeParentStyles,
          opacity: '0.16',
          bg: bg,
          zIndex: 0,
        }}
      />
      <Flex
        css={{
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          color: 'textSecondary',
          textAlign: 'center',
        }}
      >
        <Center
          css={{
            position: 'relative',
            width: [pxToRem(150), undefined, pxToRem(200)],
            height: [pxToRem(150), undefined, pxToRem(200)],
            mx: 'auto',
            mt: [pxToRem(-75), undefined, pxToRem(-100)],
          }}
        >
          <Box
            css={{
              position: 'absolute',
              width: 'inherit',
              height: 'inherit',
              bg: bg1,
              filter: 'blur(4px)',
            }}
          />
          <Image
            src={`/static/play_and_kollect/${image}`}
            alt={alt}
            css={{
              position: 'relative',
              zIndex: 2,
              maxWidth: [pxToRem(80), undefined, '100%'],
            }}
          />
        </Center>
        <Heading
          size="lg"
          css={{
            mt: [4, undefined, 6],
            mx: 6,
            borderBottom: 'thin',
            pb: [6, undefined, 8],
          }}
        >
          {alt}
        </Heading>
        <Box css={{ flex: 1, py: [6, undefined, 8], px: [4, undefined, 5], fontSize: 'md' }}>
          {description}
        </Box>
        <Box
          css={{
            borderTop: 'thin',
            pb: [5, undefined, pxToRem(30)],
            pt: [5, undefined, 6],
            fontSize: 'md',
          }}
        >
          <AppLinkWithArrow
            stylesContainer={{ justifyContent: 'center' }}
            href={SHARED_ROUTES.DOCS_PATH(href)}
          >
            Learn more
          </AppLinkWithArrow>
        </Box>
      </Flex>
    </Flex>
  );
}

function AssetsSection() {
  return (
    <Box
      css={{
        pt: [pxToRem(120), undefined, pxToRem(180)],
        pb: [pxToRem(80), pxToRem(120)],
      }}
    >
      <Container position="relative">
        <Flex flexWrap="wrap" justifyContent="center" gap={[pxToRem(110), 4, 8]}>
          {ASSETS_ITEMS.map(({ name, image, description, href, imageGradient, cardGradient }) => (
            <AssetCard
              key={name}
              bg={cardGradient}
              bg1={imageGradient}
              image={image}
              alt={name}
              description={description}
              href={href}
            />
          ))}
        </Flex>
      </Container>
    </Box>
  );
}

function CollectNFTSSection() {
  return (
    <Box css={{ pb: [10, 32] }}>
      <Container>
        <Box css={{ maxWidth: '38em', mx: 'auto', textAlign: 'center' }}>
          <Heading>
            <Heading
              as="span"
              variant="displayPrimary"
              css={{
                position: 'relative',
                top: '0.07em',
                mr: [3, 6],
              }}
            >
              Kollect
            </Heading>
            Various NFTs
          </Heading>
          <Box css={{ fontSize: ['lg'], color: 'textSecondary', mt: 5, mb: 8 }}>
            Play & Kollect is a web3 gaming experience that allows players to win rewards and
            collect NFTs.
          </Box>
          <ButtonFullWidthMobile
            href={SHARED_ROUTES.MAVIS_MARKET_COLLECTION_VX_GEAR}
            variant="displayOutline"
          >
            view on mavis market
          </ButtonFullWidthMobile>
        </Box>
        <Box css={{ mt: [4, 16], textAlign: 'center', position: 'relative' }}>
          <Grid
            css={{
              gap: 3,
              position: 'absolute',
              zIndex: 1,
              top: '50%',
              transform: 'translateY(-50%)',
              img: {
                maxWidth: 'none',
              },
            }}
          >
            <MarqueeItem direction="right">
              <Image src="/static/play_and_kollect/collect_row_1.png" alt="Cyberkongz equipments" />
            </MarqueeItem>
            <MarqueeItem direction="left">
              <Image src="/static/play_and_kollect/collect_row_2.png" alt="Cyberkongz equipments" />
            </MarqueeItem>
          </Grid>
          <Box
            css={{
              display: 'inline-block',
              mx: 'auto',
              position: 'relative',
              zIndex: 2,
              '&:before': {
                ...fullSizeParentStyles,
                display: 'block',
                content: '""',
                bg: 'radial-gradient(50% 50% at 50% 50%, #45F08C 0%, rgba(69, 240, 140, 0) 100%)',
                zIndex: 1,
                filter: 'blur(4px)',
              },
            }}
          >
            <Image
              src="/static/play_and_kollect/collect_capsule.png"
              alt="Genesis capsule"
              css={{
                display: 'inline-block',
                mx: 'auto',
                position: 'relative',
                zIndex: 1,
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

const LINKS = [
  {
    title: 'join the web3 gaming',
    emphasis: 'adventure',
    href: APP_ROUTES_ABSOLUTE.JUNGLE_ADVENTURE,
  },
  {
    title: 'check out cyberkongz vx on',
    emphasis: 'mavis market',
    href: SHARED_ROUTES.MAVIS_MARKET_COLLECTION_VX,
  },
  { title: 'be part of the community', emphasis: 'join discord!', href: SHARED_ROUTES.DISCORD },
];

function LinksSection() {
  return (
    <Box
      css={{
        pt: [20, 32],
        pb: [20, 40],
        backgroundImage: 'url(/static/play_and_kollect/links_bg.png)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <Container zIndex={2} position="relative">
        <LinksWithCuttedBorders links={LINKS} />
        <Box mt={14} css={{ textAlign: 'center', fontWeight: 'medium' }}>
          <Text css={{ textTransform: 'uppercase', color: 'primary' }}></Text>
          <Flex
            css={{
              gap: 6,
              justifyContent: 'center',
              mt: [3, 6],
              color: 'textSecondary',
            }}
          >
            <AppLinkWithArrow href={SHARED_ROUTES.DOCS}>Gitbook</AppLinkWithArrow>
            <AppLinkWithArrow href={LANDING_ROUTES.INTERACTIVE_ROADMAP}>
              Interactive Roadmap
            </AppLinkWithArrow>
            <AppLinkWithArrow href={SHARED_ROUTES.PNK_EXPLAINER_VIDEO}>
              Explainer Video
            </AppLinkWithArrow>
          </Flex>
        </Box>
      </Container>
    </Box>
  );
}

export default function PlayAndKollectPage() {
  return (
    <Box css={{ width: '100%' }}>
      <PageSEOTags pageKey="play-and-kollect" />
      <WelcomeSection />
      <SliderSection />
      <StarBackground stylesContainer={{ position: 'relative' }}>
        <GradientShadowOverlay top height={pxToRem(100)} />
        <AssetsSection />
        <CollectNFTSSection />
      </StarBackground>
      <LinksSection />
    </Box>
  );
}
